import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import router from 'next/router'

import { addLocalePrefix } from 'lib/addLocalePrefix'
import SectionLayout from '../../commons/layouts/SectionLayout'

import AreaPrefectureList from './AreaPrefectureList'
import AreaPlaceList from './AreaPlaceList'

// hooks
import { getAreaName } from 'hooks/common/lang'

import styles from './LocationSection.module.scss'
import {
  PrefectureArea,
  PrefectureInArea,
  PrefecturesResponse,
} from 'features/prefecture/types/prefecture'

interface Props {
  locations: any
  prefectures?: PrefecturesResponse
  locale: string
  countryCode: string
  onScrollLocation: () => void
}

const LocationDisplaySection: React.FC<Props> = ({
  locations,
  prefectures,
  locale,
  countryCode = 'jpn',
  onScrollLocation,
}: Props) => {
  const { t } = useTranslation()

  const [selectedPrefecture, setSelectedPrefecture] = useState<PrefectureInArea>()
  const prefectureAreas = prefectures ?? []

  return (
    <SectionLayout sectionName="location" title={t('TOP.LOCATION.TITLE')}>
      <div
        className={`${styles.prefectures} ${!!selectedPrefecture && styles.prefecturesSelected}`}
      >
        {prefectureAreas.map((item, index) => (
          <>
            {item.prefectures.map((prefecture, index) => (
              <div
                key={index}
                className={`${styles.selectedAreaWrap} ${
                  selectedPrefecture?.slug === prefecture.slug
                    ? styles.selectedAreaWrapSelected
                    : ''
                }`}
              >
                <div className={styles.selectedArea}>
                  <p className={styles.backList}>
                    <button
                      className={styles.btnBack}
                      onClick={() => setSelectedPrefecture(null)}
                      type="button"
                    >
                      {t('TOP.LOCATION.BACK_BUTTON_TEXT')}
                    </button>
                  </p>
                  {selectedPrefecture?.slug === prefecture.slug && (
                    <h3 className={styles.areaName}>
                      <Link
                        href={addLocalePrefix(`/${countryCode}/city/${prefecture.slug}`, locale)}
                        legacyBehavior
                      >
                        <a className={styles.areaNameLink}>
                          {t('TOP.LOCATION.PREFECTURE_AREA_LABEL', {
                            prefecture: prefecture.name,
                          })}
                        </a>
                      </Link>
                    </h3>
                  )}
                  <AreaPlaceList
                    prefectureSlug={prefecture.slug}
                    places={prefecture.stations}
                    label={t('TOP.LOCATION.STATION_LABEL')}
                    countryCode={countryCode}
                    locale={locale}
                  />
                  <div className={styles.stationsContent}>
                    <AreaPlaceList
                      prefectureSlug={prefecture.slug}
                      places={prefecture.facilities}
                      label={t('TOP.LOCATION.FACILITY_LABEL')}
                      countryCode={countryCode}
                      locale={locale}
                    />
                  </div>
                </div>
              </div>
            ))}
          </>
        ))}
      </div>
      <div
        className={`${styles.areaItemsWrap} ${
          !!selectedPrefecture && styles.areaItemsWrapSelected
        }`}
      >
        {locations?.length > 0 && (
          <ul className={styles.locationsItems}>
            {locations.map((location, index) => (
              <li
                className={styles.item}
                style={{
                  background: location.backgroundImage,
                  backgroundSize: 'cover',
                }}
                key={index}
              >
                <div className={styles.itemOverlay}>
                  <Link
                    href={addLocalePrefix(`/jpn/city/${location.slug}`, locale)}
                    passHref
                    legacyBehavior
                  >
                    <a className={styles.areaNameLink}>{location.name}</a>
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.areaItems}>
          {countryCode === 'jpn' &&
            prefectureAreas.length > 0 &&
            prefectureAreas.map((prefecture: PrefectureArea, index) => (
              <div className={styles.areaItem} key={index}>
                <AreaPrefectureList
                  areaName={getAreaName(locale, prefecture)}
                  prefectures={prefecture.prefectures}
                  onSelectAreaName={(_prefecture: PrefectureInArea) => {
                    setSelectedPrefecture(_prefecture)
                    onScrollLocation()
                  }}
                  locale={locale}
                  countryCode={countryCode}
                />
              </div>
            ))}
          {countryCode === 'twn' && prefectureAreas.length > 0 && (
            <>
              <div className={styles.gridLocationContainer}>
                {prefectureAreas.map((item, index) => {
                  return (
                    <div className={styles.location} key={index}>
                      <p className={styles.area}>{item.area_name}</p>

                      <div className={styles.cityContainer}>
                        {(item.prefectures || []).map((prefecture, index) => {
                          return (
                            <p
                              onClick={() =>
                                router.push(addLocalePrefix(`/twn/city/${prefecture.slug}`, locale))
                              }
                              className={styles.city}
                              key={index}
                            >
                              {prefecture.name}
                            </p>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </SectionLayout>
  )
}

export default LocationDisplaySection
